/**
 * Resource Action - WHAT Section
 *
 * Edit mode UI for assigning a resource.
 * Used inline within AutomationCard when editing the "What" section.
 *
 * V2 Reference: v2/app/templates/components/client-reminder-resources.hbs
 *
 * Fields:
 * - reminderOnId: Selected resource ID
 * - reminderOnType: 'Asset::Resource' (set automatically)
 */

import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Stack } from 'src/components/Stack';
import { fetchResources } from 'src/models/resource/resource';
import { FormSelect } from 'src/shared/Forms/Fields/FormSelect';

import { ActionWhatSectionProps } from '../automationActions';

/**
 * Resource WHAT Section - Edit mode
 */
export const ResourceWhatSection: FC<ActionWhatSectionProps> = ({
  index,
}) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();

  // Fetch available resources
  const { data: resources = [], isLoading } = useQuery({
    queryKey: ['resources'],
    queryFn: () => fetchResources({ fetchAll: true }),
    staleTime: 60000,
  });

  const resourceOptions = resources.map((r) => ({
    value: r.id.toString(),
    label: r.caption || r.name || `Resource #${r.id}`,
  }));

  const handleResourceChange = (resourceId: string) => {
    setFieldValue(`automations.${index}.reminderOnId`, resourceId);
    setFieldValue(`automations.${index}.reminderOnType`, 'Asset::Resource');
  };

  return (
    <Stack spacing={2}>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Box>{t('automations.resource.assign')}</Box>
        <Box sx={{ minWidth: 200 }}>
          <FormSelect
            name={`automations.${index}.reminderOnId`}
            options={resourceOptions}
            prompt={isLoading ? t('automations.common.loading') : t('automations.resource.selectResource')}
            onChange={(e) => handleResourceChange(e.target.value as string)}
          />
        </Box>
      </Box>
    </Stack>
  );
};

/**
 * Resource What Summary Component
 */
export const ResourceWhatSummary: FC<{
  automation: ActionWhatSectionProps['automation'];
}> = ({ automation }) => {
  const { t } = useTranslation();
  const { data: resources = [] } = useQuery({
    queryKey: ['resources'],
    queryFn: () => fetchResources({ fetchAll: true }),
    staleTime: 60000,
  });

  if (!automation.reminderOnId) {
    return <i>{t('automations.resource.noResourceSelected')}</i>;
  }

  const resource = resources.find(
    (r) => r.id.toString() === automation.reminderOnId?.toString()
  );
  const resourceName =
    resource?.caption || resource?.name || `Resource #${automation.reminderOnId}`;

  return (
    <>
      {t('automations.resource.assign')} <b>{resourceName}</b>
    </>
  );
};
